import { Injectable, inject } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { Device, DeviceInfo } from '@capacitor/device';
import { environment } from '../../../environments/environment';

export type MobilePlatform = 'android' | 'ios' | 'web';

export interface PlatformInfo {
    platform: MobilePlatform;
    isNative: boolean;
    isAndroid: boolean;
    isIOS: boolean;
    isWeb: boolean;
    nativeVersion: string | null;
}

/**
 * Serviço central de detecção e abstração de plataforma.
 *
 * Responsabilidade única (SRP): responder "onde estou rodando?"
 * Aberto para extensão via PlatformInfo, fechado para modificação (OCP).
 *
 * Uso:
 *   if (this.platform.isNative()) { ... }
 *   if (this.platform.isAndroid()) { ... }
 */
@Injectable({ providedIn: 'root' })
export class MobilePlatformService {
    private readonly _platform: MobilePlatform =
        Capacitor.getPlatform() as MobilePlatform;

    isAndroid(): boolean {
        return this._platform === 'android';
    }

    isIOS(): boolean {
        return this._platform === 'ios';
    }

    isNative(): boolean {
        return Capacitor.isNativePlatform();
    }

    isWeb(): boolean {
        return this._platform === 'web';
    }

    getPlatform(): MobilePlatform {
        return this._platform;
    }

    isPluginAvailable(pluginName: string): boolean {
        return Capacitor.isPluginAvailable(pluginName);
    }

    async getDeviceInfo(): Promise<DeviceInfo | null> {
        if (!this.isNative()) return null;
        return Device.getInfo();
    }

    getPlatformInfo(): PlatformInfo {
        return {
            platform: this._platform,
            isNative: this.isNative(),
            isAndroid: this.isAndroid(),
            isIOS: this.isIOS(),
            isWeb: this.isWeb(),
            nativeVersion: (environment as any).version ?? null,
        };
    }
}
